const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8000"
).replace(/\/$/, "")

const API_URL = API_BASE_URL.endsWith("/api")
  ? API_BASE_URL
  : `${API_BASE_URL}/api`


/* =========================================================
   OUTILS INTERNES
   ========================================================= */

function safeJsonParse(value, fallback = {}) {
  try {
    return JSON.parse(value || "{}")
  } catch {
    return fallback
  }
}


function getLocalUser() {
  return safeJsonParse(
    localStorage.getItem("kongosys_user"),
    {}
  )
}


function buildHeaders() {
  const user = getLocalUser()

  const userId =
    localStorage.getItem("kongosys_user_id") ||
    user.id ||
    ""

  const role =
    localStorage.getItem("kongosys_role") ||
    user.role ||
    ""

  const companyId =
    localStorage.getItem("kongosys_company_id") ||
    user.company_id ||
    "1"

  const organizationId =
    localStorage.getItem("kongosys_organization_id") ||
    user.organization_id ||
    ""

  const organizationCode =
    localStorage.getItem("kongosys_organization_code") ||
    user.organization_code ||
    ""

  const storeId =
    localStorage.getItem("kongosys_store_id") ||
    user.store_id ||
    ""

  const warehouseId =
    localStorage.getItem("kongosys_warehouse_id") ||
    user.warehouse_id ||
    ""

  return {
    "Content-Type": "application/json",

    "x-kongosys-user-id": String(userId || ""),
    "x-user-id": String(userId || ""),

    "x-kongosys-role": String(role || ""),
    "x-user-role": String(role || ""),
    "x-role": String(role || ""),

    "x-kongosys-company-id": String(companyId || "1"),
    "x-company-id": String(companyId || "1"),

    "x-kongosys-organization-id": String(
      organizationId || ""
    ),
    "x-organization-id": String(
      organizationId || ""
    ),

    "x-kongosys-organization-code": String(
      organizationCode || ""
    ),
    "x-organization-code": String(
      organizationCode || ""
    ),

    "x-kongosys-store-id": String(storeId || ""),
    "x-store-id": String(storeId || ""),

    "x-kongosys-warehouse-id": String(
      warehouseId || ""
    ),
    "x-warehouse-id": String(
      warehouseId || ""
    ),
  }
}


async function request(path, options = {}) {
  const cleanPath = path.startsWith("/")
    ? path
    : `/${path}`

  const url = `${API_URL}${cleanPath}`

  const response = await fetch(url, {
    ...options,

    headers: {
      ...buildHeaders(),
      ...(options.headers || {}),
    },
  })

  let data = null

  const contentType =
    response.headers.get("content-type") || ""

  if (response.status !== 204) {
    if (contentType.includes("application/json")) {
      data = await response
        .json()
        .catch(() => null)
    } else {
      data = await response
        .text()
        .catch(() => null)
    }
  }

  if (!response.ok) {
    let message = `Erreur API entrepôts (${response.status}).`

    if (typeof data === "string" && data.trim()) {
      message = data
    } else if (data?.detail) {
      if (typeof data.detail === "string") {
        message = data.detail
      } else {
        try {
          message = JSON.stringify(data.detail)
        } catch {
          message = "Erreur API entrepôts."
        }
      }
    } else if (data?.message) {
      message = data.message
    }

    const error = new Error(message)

    error.status = response.status
    error.data = data
    error.url = url

    throw error
  }

  return data
}


function buildQuery(params = {}) {
  const query = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      value !== "undefined" &&
      value !== "null"
    ) {
      query.set(key, String(value))
    }
  })

  const queryString = query.toString()

  return queryString
    ? `?${queryString}`
    : ""
}


/* =========================================================
   NORMALISATION TABLEAUX API
   ========================================================= */

export function normalizeApiArray(
  data,
  preferredKey = ""
) {
  if (Array.isArray(data)) {
    return data
  }

  if (
    preferredKey &&
    data &&
    Array.isArray(data[preferredKey])
  ) {
    return data[preferredKey]
  }

  if (Array.isArray(data?.items)) {
    return data.items
  }

  if (Array.isArray(data?.data)) {
    return data.data
  }

  if (Array.isArray(data?.results)) {
    return data.results
  }

  if (Array.isArray(data?.warehouses)) {
    return data.warehouses
  }

  if (Array.isArray(data?.products)) {
    return data.products
  }

  if (Array.isArray(data?.stores)) {
    return data.stores
  }

  if (Array.isArray(data?.stock)) {
    return data.stock
  }

  if (Array.isArray(data?.movements)) {
    return data.movements
  }

  if (Array.isArray(data?.recent_movements)) {
    return data.recent_movements
  }

  if (Array.isArray(data?.recent_receipts)) {
    return data.recent_receipts
  }

  return []
}


/* =========================================================
   DASHBOARD ENTREPÔTS
   ========================================================= */

export function getWarehouseDashboard(
  params = {}
) {
  const query = buildQuery({
    warehouse_id:
      params.warehouse_id !== undefined &&
      params.warehouse_id !== null &&
      params.warehouse_id !== ""
        ? params.warehouse_id
        : undefined,

    low_stock_threshold:
      params.low_stock_threshold !== undefined &&
      params.low_stock_threshold !== null &&
      params.low_stock_threshold !== ""
        ? params.low_stock_threshold
        : undefined,

    recent_limit:
      params.recent_limit !== undefined &&
      params.recent_limit !== null &&
      params.recent_limit !== ""
        ? params.recent_limit
        : undefined,
  })

  return request(
    `/warehouses/dashboard${query}`
  )
}


/* =========================================================
   ENTREPÔTS
   ========================================================= */

export function getWarehouses() {
  return request("/warehouses")
}


export function getWarehouseById(id) {
  if (!id) {
    return Promise.reject(
      new Error("Identifiant entrepôt manquant.")
    )
  }

  return request(`/warehouses/${id}`)
}


export function getWarehouseFormData() {
  return request(
    "/warehouses/form-data"
  )
}


/* =========================================================
   STOCK ENTREPÔTS
   ========================================================= */

export function getWarehouseStockItems(
  params = {}
) {
  const query = buildQuery({
    warehouse_id:
      params.warehouse_id,

    search:
      params.search,

    include_zero:
      params.include_zero === true
        ? "true"
        : undefined,
  })

  return request(
    `/warehouses/stock-items${query}`
  )
}


/* =========================================================
   CRÉATION ENTREPÔT
   ========================================================= */

export function createWarehouse(payload) {
  return request("/warehouses", {
    method: "POST",
    body: JSON.stringify(payload),
  })
}


/* =========================================================
   MODIFICATION ENTREPÔT
   ========================================================= */

export function updateWarehouse(
  id,
  payload
) {
  if (!id) {
    return Promise.reject(
      new Error("Identifiant entrepôt manquant.")
    )
  }

  return request(
    `/warehouses/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    }
  )
}


/* =========================================================
   DÉSACTIVATION ENTREPÔT
   ========================================================= */

export function deactivateWarehouse(id) {
  if (!id) {
    return Promise.reject(
      new Error("Identifiant entrepôt manquant.")
    )
  }

  return request(
    `/warehouses/${id}/deactivate`,
    {
      method: "POST",
      body: JSON.stringify({}),
    }
  )
}


/* =========================================================
   AJOUT MANUEL STOCK
   ========================================================= */

export function addWarehouseStock(
  payload
) {
  return request(
    "/warehouses/stock-in",
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  )
}


/* =========================================================
   TRANSFERT ENTREPÔT → SHOP
   ========================================================= */

export function transferWarehouseToStore(
  payload
) {
  return request(
    "/warehouses/transfer-to-store",
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  )
}


/* =========================================================
   TRANSFERT ENTREPÔT → ENTREPÔT
   ========================================================= */

export function transferWarehouseToWarehouse(
  payload
) {
  return request(
    "/warehouses/transfer-to-warehouse",
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  )
}